import type { Activity } from "@/api/queries";

/**
 * What the agenda knows about time and colour, in one place.
 *
 * Kept out of the components because three densities — day, week, month — draw
 * the same events from the same rules, and a rule copied into three files is a
 * rule that will disagree with itself.
 */

/** Visits: go out. `{0, 4}` — `VISIT` and `VISIT_SCHEDULED`. */
const VISITS = new Set([0, 4]);
/**
 * Calls: dial. `{1, 3, 5, 7, 8, 9, 13, 14}` — the last two the videoconference
 * and its *a futuro*, which is a call with video.
 */
const CALLS = new Set([1, 3, 5, 7, 8, 9, 13, 14]);

/**
 * The colour of an event, by **channel** and never by funnel stage (D24).
 *
 * Keyed to the legacy `Activity.Kind` integers, like every other mapping in
 * this codebase: the labels are Spanish prose that will get reworded, the
 * integers are values fifteen years of history depend on. A relabelled kind
 * cannot silently change colour.
 *
 * One axis on the neutral ramp — go out, dial, or write — which is what frees
 * the accent for today's column and the primary action.
 */
export function channelOf(kind: number): "visit" | "call" | "message" {
  if (VISITS.has(kind)) return "visit";
  if (CALLS.has(kind)) return "call";
  return "message";
}

/** The bar down the left of an event, as a CSS colour. */
export const CHANNEL_BAR: Record<ReturnType<typeof channelOf>, string> = {
  visit: "var(--color-text)",
  call: "var(--color-neutral-600)",
  message: "var(--color-neutral-400)",
};

/** The system tag class the same channel uses on a label. */
export const CHANNEL_TAG: Record<
  ReturnType<typeof channelOf>,
  "outline" | "neutral"
> = {
  visit: "outline",
  call: "neutral",
  message: "neutral",
};

export type Density = "day" | "week" | "month";

/* ── Dates ───────────────────────────────────────────────────────────────
 *
 * Local time throughout. `due_at` is an instant and the seller reads it on a
 * clock in Monterrey, so every boundary here is computed in the browser's zone
 * and only converted to an instant at the edge, where the query is built.
 */

export function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function endOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

/**
 * Monday of the week `date` falls in.
 *
 * Monday, not Sunday: the design's grid is five columns, *Lun* to *Vie*, and
 * a sales week that starts on Sunday would push Friday off the end of it.
 */
export function startOfWeek(date: Date): Date {
  const copy = startOfDay(date);
  const weekday = copy.getDay(); // 0 = Sunday
  copy.setDate(copy.getDate() - (weekday === 0 ? 6 : weekday - 1));
  return copy;
}

export function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

export function startOfMonth(date: Date): Date {
  const copy = startOfDay(date);
  copy.setDate(1);
  return copy;
}

export function addMonths(date: Date, months: number): Date {
  const copy = startOfMonth(date);
  copy.setMonth(copy.getMonth() + months);
  return copy;
}

/** The half-open window a density asks the server for, as inclusive instants. */
export function rangeFor(density: Density, anchor: Date): { from: Date; to: Date } {
  if (density === "day") return { from: startOfDay(anchor), to: endOfDay(anchor) };
  if (density === "week") {
    const monday = startOfWeek(anchor);
    // Five working days. Saturday and Sunday are not drawn, and asking for
    // them would put events in the response that no column can hold.
    return { from: monday, to: endOfDay(addDays(monday, 4)) };
  }
  const first = startOfMonth(anchor);
  // The grid shows whole weeks, so it spills into the neighbouring months and
  // the request has to cover what is drawn rather than what the month is.
  const gridStart = startOfWeek(first);
  return { from: gridStart, to: endOfDay(addDays(gridStart, 41)) };
}

/** `2026-08-27` — a stable key for grouping events by local day. */
export function dayKey(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}

/**
 * An activity with a date to be on the agenda by.
 *
 * `due_at` is when booked work is due, and it is empty on what a call or an
 * email logged as already done: that was never booked. The agenda and the
 * day's panel only ever hold dated work — the server finds it by that date —
 * so they narrow once, where the rows come in, and nothing past that has to
 * wonder.
 */
export type Scheduled = Activity & { due_at: string };

export function isScheduled(activity: Activity): activity is Scheduled {
  return activity.due_at !== null;
}

/** Events for one local day, in time order. */
export function eventsOn(events: Scheduled[], day: Date): Scheduled[] {
  const key = dayKey(day);
  return events
    .filter((event) => dayKey(new Date(event.due_at)) === key)
    .sort((a, b) => a.due_at.localeCompare(b.due_at));
}

/**
 * The five two-hour bands of the design's grid, plus a sixth for everything
 * outside them.
 *
 * The artboard draws 09:00 to 19:00 and nothing else, but `due_at` accepts any
 * time and fifteen years of imported history is full of 08:30 and 20:15. An
 * event with no band would simply not be drawn, so *Fuera de horario* exists
 * and renders only when something lands in it — the grid keeps its shape on
 * the ordinary week and stops lying on the unusual one.
 */
export const BANDS = [9, 11, 13, 15, 17] as const;
export const OUT_OF_HOURS = -1;

export function bandOf(date: Date): number {
  const hour = date.getHours();
  for (const band of BANDS) {
    if (hour >= band && hour < band + 2) return band;
  }
  return OUT_OF_HOURS;
}

/** Whether any event in the set falls outside the drawn bands. */
export function hasOutOfHours(events: Scheduled[]): boolean {
  return events.some((event) => bandOf(new Date(event.due_at)) === OUT_OF_HOURS);
}
